<template>
  <div class="pt-32 md:pt-64 pb-72">
    <Skeleton v-if="isLoading" />
    <div v-else>
      <RevContainer>
        <div class="mx-auto max-w-[736px]">
          <p class="punchline-sans text-center">
            {{ i18n(translations.pageTitle) }}
          </p>
          <h1 class="heading-3 text-static-default-low mt-16 text-center">
            {{ title }}
          </h1>
          <div v-if="category" class="mt-16 flex justify-center">
            <RevButton :to="category.url" variant="primary">
              {{
                i18n(translations.shopCategory, { category: category.label })
              }}
            </RevButton>
          </div>
          <Reassurance class="mt-56 text-center" />

          <div class="mx-auto mt-56">
            <div
              ref="bodyContainer"
              class="grid grid-cols-2 gap-x-24 lg:gap-x-40"
            >
              <ProductCard
                v-for="product in products"
                :key="`card-${product.productId}`"
                :product
              />
            </div>
            <TableContent
              ref="bottomSentinel"
              class="mt-32"
              :ratings
              :table-data
            />
          </div>
        </div>
      </RevContainer>
      <RevContainer class="fixed top-0 z-[1] inset-x-0">
        <div class="mx-auto max-w-[736px]">
          <Transition
            enter-active-class="ease-out duration-150"
            enter-from-class="-translate-y-full"
            enter-to-class="translate-y-0"
            leave-active-class="ease-in duration-150"
            leave-from-class="translate-y-0"
            leave-to-class="-translate-y-full"
          >
            <div
              v-show="isScrolled && !isAtBottom"
              class="bg-surface-default-low border-static-default-low border-b"
            >
              <div
                class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-16"
              >
                <ProductCard
                  v-for="product in products"
                  :key="`sticky-card-${product.productId}`"
                  :product
                  sticky
                />
              </div>
            </div>
          </Transition>
        </div>
      </RevContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { useIntersectionObserver, useWindowScroll } from '@vueuse/core'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import { useEnrichResultsPage } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage'
import type { ComparisonData } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage.types'
import { useModelsComparisonHead } from '../../../composables/useModelsComparisonHeadV2/useModelsComparisonHeadV2'
import ProductCard from '../ProductCard/ProductCard.vue'
import Reassurance from '../Reassurance/Reassurance.vue'
import Skeleton from '../Skeleton/Skeleton.vue'
import TableContent from '../TableContent/TableContent.vue'

import translations from './Main.translations'

const props = defineProps<{
  comparisonData: ComparisonData | null | undefined
  comparisonPending: boolean
}>()

const { products, ratings, tableData, pending, category } =
  useEnrichResultsPage(
    toRef(props, 'comparisonData'),
    toRef(props, 'comparisonPending'),
  )

const i18n = useI18n()
const isScrolled = ref(false)
const isAtBottom = ref(false)
const bodyContainer = ref()
const bottomSentinel = ref()

const title = computed(() =>
  i18n(translations.resultsTitle, {
    firstModel: products.value[0]?.title,
    secondModel: products.value[1]?.title,
  }),
)

const isLoading = computed(() => pending.value || !products.value.length)

useIntersectionObserver(
  bodyContainer,
  ([{ boundingClientRect }]) => {
    isScrolled.value = boundingClientRect.top < -100
  },
  { threshold: [0, 1] },
)

const { y: scrollY } = useWindowScroll()
watch(scrollY, () => {
  const el = bottomSentinel.value?.$el ?? bottomSentinel.value
  if (!el) return
  isAtBottom.value = el.getBoundingClientRect().bottom <= window.innerHeight / 2
})

const productNames = computed(() =>
  products.value.length >= 2
    ? [products.value[0].title, products.value[1].title]
    : null,
)
useModelsComparisonHead(productNames)

useAddCanonicalLink()
</script>
